import { SimpleDocCommand } from '../../DocsApp/commands/v5/types';
import { CommandExecutionContext } from '../../../types/commands';
import fetchService from '../../../services/fetchService';
import { API_BASE_URL } from '../../../api/config';
import { schemaToParameters } from '../../../utils/schemaToParameters';
import { TIER_CADENCE_DAYS, getWarmthLevel } from '../types/entity';
import type { ContactTier } from '../types/entity';

export const contacts_find: SimpleDocCommand = {
  id: 'contacts_find',
  logicalName: 'contacts_find',
  version: 'v1',

  description: `Search and filter contacts by name, email, tier, tag, or staleness. Returns contacts with their skeleton fields.

Use contacts_find to look up contacts before emailing them, build briefings, or check relationship status.

**TIER VALUES:** all (default) | close | regular | archived

**EXAMPLES:**

Find close contacts:
{ "tier": "close" }

Search by name or email:
{ "query": "alice" }

Get close contacts with notes:
{ "tier": "close", "includeNotes": true }

Find contacts overdue for outreach:
{ "staleOnly": true }

Filter by tag:
{ "tag": "investor" }

**RETURNS:** Array of contacts with id, llmId, name, and skeleton (email, phone, company, tier, tags, etc.).`,

  schema: {
    parameters: schemaToParameters({
      type: 'object',
      properties: {
        query:        { type: 'string',  description: 'Search by name or email (partial match)' },
        tier: {
          type: 'string',
          enum: ['all', 'close', 'regular', 'archived'],
          description: 'Filter by relationship tier. "all" returns all contacts (default).',
          default: 'all',
        },
        tag:          { type: 'string',  description: 'Filter by tag' },
        staleOnly:    { type: 'boolean', description: 'Return only contacts overdue for engagement based on tier cadence' },
        includeNotes: { type: 'boolean', description: 'Include relationship notes in results (default: false)', default: false },
        limit:        { type: 'number',  description: 'Max contacts to return (default 100)', default: 100 },
      },
      required: [],
    }),
  },

  handler: async (params: any, context: CommandExecutionContext) => {
    const {
      query, tier, tag, stale_only,
      channel, warmth, no_contact_since_days,
      has_handle, identity_type,
    } = params;

    // Build query params for backend filtering where possible
    const urlParams = new URLSearchParams();
    if (channel) urlParams.set('channel', channel);
    if (identity_type) urlParams.set('identity_type', identity_type);
    if (has_handle) urlParams.set('has_handle', has_handle);
    if (warmth) urlParams.set('warmth', warmth);
    if (stale_only) urlParams.set('stale_only', 'true');
    if (no_contact_since_days) urlParams.set('no_contact_since_days', String(no_contact_since_days));

    const url = `${API_BASE_URL}/contacts-app${urlParams.toString() ? `?${urlParams}` : ''}`;
    let contacts: any[] = await fetchService(url);
    if (!Array.isArray(contacts)) contacts = [];

    // Client-side fine-grained filtering
    if (tier) contacts = contacts.filter(c => c.skeleton?.tier === tier);
    if (query) {
      const q = query.toLowerCase();
      contacts = contacts.filter(c =>
        c.name?.toLowerCase().includes(q) ||
        c.skeleton?.email?.toLowerCase().includes(q) ||
        c.skeleton?.handles?.reddit?.toLowerCase().includes(q) ||
        c.skeleton?.handles?.twitter?.toLowerCase().includes(q)
      );
    }
    if (tag) contacts = contacts.filter(c => c.skeleton?.tags?.includes(tag));

    // Stale filter (client-side fallback)
    if (stale_only && !urlParams.has('stale_only')) {
      const now = Date.now();
      contacts = contacts.filter(c => {
        const cadence = TIER_CADENCE_DAYS[c.skeleton?.tier as ContactTier];
        if (!cadence) return false;
        if (!c.skeleton?.lastInteraction?.at) return true;
        return (now - new Date(c.skeleton.lastInteraction.at).getTime()) / 86400000 >= cadence;
      });
    }

    const summary = contacts.map(c => ({
      id: c._id,
      llmId: c.llmId,
      name: c.name,
      email: c.skeleton?.email,
      company: c.skeleton?.company,
      tier: c.skeleton?.tier,
      tags: c.skeleton?.tags,
      handles: c.skeleton?.handles,
      primaryChannel: c.skeleton?.primaryChannel,
      primaryChannelContext: c.skeleton?.primaryChannelContext,
      warmthScore: c.skeleton?.warmthScore,
      warmthLevel: c.skeleton?.warmthScore !== undefined ? getWarmthLevel(c.skeleton.warmthScore) : undefined,
      lastInteractionAt: c.skeleton?.lastInteraction?.at,
    }));

    return {
      status: 'success' as const,
      message: `Found ${summary.length} contact${summary.length !== 1 ? 's' : ''}`,
      data: { contacts: summary, count: summary.length },
      format: 'json' as const,
    };
  },
};
